import React, { useMemo } from "react"
import { Box, Text, Flex } from "@chakra-ui/react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation } from "swiper"
import "swiper/css/navigation"

import { useCartContext } from "@app/providers/cart"
import { useCartUpsell } from "@app/hooks/useCartUpsell"
import CarouselButton from "@app/components/CarouselButton"
import { CartUpsellProductCard } from "@app/components/Cart/CartUpsellProductCard"
import type { CartLine } from "@shopify/hydrogen-react/storefront-api-types"

interface CartProgressProps {
  cartMiniProgress?: number
}

export const CartUpsell: React.FC<CartProgressProps> = React.memo(() => {
  const { products, title } = useCartUpsell()
  const { cart } = useCartContext()

  const upsellCompleted = useMemo(() => {
    if (!products || products.length === 0) return false
    return products.every(product => cart?.lines.some((lineItem: CartLine) => lineItem?.merchandise.id === product?.variants?.[0]?.id))
  }, [products, cart?.lines])

  const filteredLiveProducts = useMemo(() => {
    if (!products) return []
    return products
      .filter(product => !cart?.lines.some((lineItem: CartLine) => lineItem?.merchandise.id === product?.variants?.[0]?.id))
      ?.filter(product => product?.variants?.[0]?.availableForSale)
  }, [products, cart?.lines])

  if (upsellCompleted || !filteredLiveProducts?.length) return null

  return (
    <Box borderTop="1px solid" borderTopColor="primary.softGrey" pt={6}>
      <Flex direction="row" justifyContent="space-between" mb={3}>
        <Text size="lg">{title}</Text>
        {filteredLiveProducts.length > 1 && (
          <Flex
            gap={{ base: 12, md: 13 }}
            position="relative"
            alignItems="flex-end"
            sx={{
              ".cart-upsell-swiper-button-next::after, .cart-upsell-swiper-button-prev::after": {
                content: '""',
              },
              ".cart-upsell-swiper-button-prev.swiper-disabled, .cart-upsell-swiper-button-next.swiper-disabled": {
                opacity: 0.3,
                pointerEvents: "none",
              },
            }}
          >
            <CarouselButton
              className="cart-upsell-swiper-button-prev"
              direction="prev"
              icon="carouselChevronPair"
              label="Previous suggested product"
              variant="onlyIcon"
            />

            <CarouselButton
              className="cart-upsell-swiper-button-next"
              direction="next"
              icon="carouselChevronPair"
              label="Next suggested product"
              variant="onlyIcon"
            />
          </Flex>
        )}
      </Flex>

      <Swiper
        modules={[Navigation]}
        slidesPerView={filteredLiveProducts.length > 1 ? 1.1 : 1}
        spaceBetween={8}
        breakpoints={{ 768: { spaceBetween: 12 } }}
        simulateTouch={false}
        pagination={{ clickable: true }}
        navigation={{
          nextEl: ".cart-upsell-swiper-button-next",
          prevEl: ".cart-upsell-swiper-button-prev",
          disabledClass: "swiper-disabled",
        }}
      >
        {filteredLiveProducts.map((product, index: number) => (
          <SwiperSlide key={`cart-upsell-product-${index}`} style={{ alignSelf: "center" }}>
            <CartUpsellProductCard product={product} key={`${index}-${product?.handle}`} />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  )
})
